import { Component, inject, Input, signal, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { ToastService } from '../../../core/services/toast.service';
import { VendorsService } from '../../../core/services/vendors.service';
import { authActions } from '../../../core/store/auth';
import { ApiError } from '../../../core/models/common/api-error';
import { User } from '../../../core/models/entities/user';
import { FilePondOptions } from 'filepond';
import { FileUploadComponent } from '../../../shared-manager/components/file-upload/file-upload.component';
import { SharedModule } from '../../../shared/shared.module';
import { SharedManagerModule } from '../../../shared-manager/shared-manager.module';

@Component({
  selector: 'app-vendor-terms-settings',
  template: `
    <app-fieldset-card
      [showSaveButton]="true"
      [saving]="saving()"
      (onSave)="save()"
    >
      <div title>Conditions générales</div>
      <div description>
        Vous pouvez importer ici un document au format PDF de vos conditions
        générales. Le client sera amené à accepter ces conditions générales
        pour pouvoir valider sa demande de réservation. Ces conditions
        générales sont valables pour toutes les salles, sauf pour celles où
        des conditions spéciales ont été ajoutées dans le menu de gestion de
        la salle.
      </div>

      <app-file-upload
        #generalTermsUploadCmp
        [config]="generalTermsUploadConfig"
        [defaultFiles]="
          authUser?.vendor?.general_terms_public_url
            ? [authUser?.vendor?.general_terms_public_url]
            : []
        "
      ></app-file-upload>
    </app-fieldset-card>
  `,
  imports: [SharedModule, SharedManagerModule],
})
export class VendorTermsSettingsComponent {
  @Input() authUser!: User;

  @ViewChild('generalTermsUploadCmp')
  generalTermsUploadCmp: FileUploadComponent;

  private store = inject(Store);
  private toastService = inject(ToastService);
  private vendorsService = inject(VendorsService);

  saving = signal<boolean>(false);

  readonly generalTermsUploadConfig: Partial<FilePondOptions> = {
    className: 'single-col',
    allowMultiple: false,
    acceptedFileTypes: ['application/pdf'],
    onactivatefile: () => {
      if (
        this.authUser.vendor.general_terms_public_url &&
        typeof this.generalTermsUploadCmp.files[0] === 'string'
      ) {
        window.open(this.authUser.vendor.general_terms_public_url, '_blank');
      }
    },
    labelIdle: `
      <div>
        <i class="fal fa-file-pdf"></i>
        <div class="font-sans text-secondary-500">Faites glisser les conditions générales ici <br>ou <span class="underline cursor-pointer">téléchargez-les depuis votre appareil</span>.</div>
      </div>
    `,
  };

  async save() {
    this.saving.set(true);
    try {
      const vendor = await this.vendorsService.update({
        name: this.authUser.vendor.name,
        slug: this.authUser.vendor.slug,
        general_terms:
          typeof this.generalTermsUploadCmp.files[0] === 'string'
            ? null
            : this.generalTermsUploadCmp.files[0],
        delete_general_terms: !!(
          this.generalTermsUploadCmp.files.length === 0 &&
          this.authUser.vendor.general_terms_public_url
        ),
      });
      this.updateUserStore(vendor);
      this.toastService.success('Conditions générales enregistrées.');
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
    } finally {
      this.saving.set(false);
    }
  }

  private updateUserStore(vendor: any) {
    this.store.dispatch(
      authActions.findCurrentUserSuccess({
        user: {
          ...this.authUser,
          vendor: { ...this.authUser.vendor, ...vendor },
        },
      }),
    );
  }
}
